import React from "react";
import { Track } from "livekit-client";
import { useTracks, useParticipants, VideoTrack, useIsSpeaking, useConnectionState } from "@livekit/components-react";
import { MicOff, VideoOff, Loader2, WifiOff } from "lucide-react";
import { AuraRing } from "@/components/brand/AuraRing";
import { mediaUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

function meta(p) { try { return JSON.parse(p.metadata || "{}"); } catch { return {}; } }

function Tile({ participant, big, cameraRef }) {
  const speaking = useIsSpeaking(participant);
  const m = meta(participant);
  const camOn = cameraRef && !cameraRef.publication?.isMuted;
  const micPub = participant.getTrackPublication(Track.Source.Microphone);
  const micOff = !micPub || micPub.isMuted;
  return (
    <div data-testid={`stage-tile-${participant.identity}`} className={cn("relative h-full w-full overflow-hidden rounded-2xl bg-am-surface2 transition-shadow", speaking && "ring-2 ring-aura-cyan shadow-[0_0_30px_-8px_rgba(56,225,255,0.8)]")}>
      {camOn ? <VideoTrack trackRef={cameraRef} className="h-full w-full object-cover" /> : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-b from-am-surface to-black">
          <AuraRing src={mediaUrl(m.picture) || undefined} size={big ? 96 : 56} ring={2} fallback={participant.name} />
          <span className="flex items-center gap-1 text-xs text-am-text2"><VideoOff className="h-3 w-3" /> Camera off</span>
        </div>
      )}
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur">
        {participant.name || m.username}{micOff && <MicOff className="h-3 w-3 text-aura-magenta" />}
      </div>
    </div>
  );
}

// Renders the on-stage hosts according to the broadcast layout; active speaker gets the aura ring.
export function LiveStage({ layout = "side_by_side", spotlight, hostIds = [] }) {
  const state = useConnectionState();
  const participants = useParticipants();
  const cams = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const hosts = participants.filter((p) => hostIds.includes(p.identity) || meta(p).role === "host");
  const camFor = (p) => cams.find((t) => t.participant.identity === p.identity);

  if (state === "connecting" || state === "reconnecting") {
    return <div data-testid="stage-reconnecting" className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-2xl bg-am-surface2 text-am-text2"><Loader2 className="h-7 w-7 animate-spin aura-text" /><span className="text-sm font-semibold">{state === "reconnecting" ? "Reconnecting…" : "Connecting…"}</span></div>;
  }
  if (state === "disconnected") {
    return <div data-testid="stage-disconnected" className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-2xl bg-am-surface2 text-am-text2"><WifiOff className="h-7 w-7" /><span className="text-sm font-semibold">Disconnected</span></div>;
  }
  if (hosts.length === 0) {
    return <div data-testid="stage-waiting" className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-2xl bg-am-surface2 text-am-text2"><Loader2 className="h-6 w-6 animate-spin" /><span className="text-sm">Waiting for the hosts…</span></div>;
  }
  const main = hosts.find((p) => p.identity === spotlight) || hosts[0];
  const rest = hosts.filter((p) => p !== main);
  if (layout === "solo" || hosts.length === 1) {
    return <div className="h-full w-full"><Tile participant={main} big cameraRef={camFor(main)} /></div>;
  }
  if (layout === "spotlight") {
    return (
      <div className="relative h-full w-full">
        <Tile participant={main} big cameraRef={camFor(main)} />
        <div className="absolute bottom-3 right-3 h-[28%] w-[34%] max-w-[220px]">{rest[0] && <Tile participant={rest[0]} cameraRef={camFor(rest[0])} />}</div>
      </div>
    );
  }
  return (
    <div className="grid h-full w-full grid-cols-1 gap-2 portrait:grid-rows-2 landscape:grid-cols-2 sm:grid-cols-2 sm:grid-rows-1">
      {hosts.slice(0, 2).map((p) => <Tile key={p.identity} participant={p} cameraRef={camFor(p)} />)}
    </div>
  );
}
